import { useCallback, useEffect, useRef, useState } from 'react';

import { NOTIFICATION_DEFAULT_ERROR_MESSAGE } from 'application/providers/NotificationProvider';

import { PromoSelectionDraftAction, useMediaPromoSelectionDraftMutation } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

type UsePromoReportDraftSessionOptions = {
  companyId: string | undefined;
  isPromoReportScope: boolean;
};

const getDraftError = (message?: string) => new Error(message || NOTIFICATION_DEFAULT_ERROR_MESSAGE);

export const usePromoReportDraftSession = ({ companyId, isPromoReportScope }: UsePromoReportDraftSessionOptions) => {
  const draftIdRef = useRef<string | undefined>(undefined);
  const isDisposedRef = useRef(false);
  const isMountedRef = useRef(true);
  const isCreateInFlightRef = useRef(false);
  const disposePromiseRef = useRef<Promise<void> | undefined>(undefined);
  const [draftId, setDraftId] = useState<string>();
  const [draftError, setDraftError] = useState<Error>();
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [updateDraft] = useMediaPromoSelectionDraftMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const createDraft = useCallback(async () => {
    if (draftIdRef.current || isCreateInFlightRef.current || isDisposedRef.current) {
      return;
    }

    if (!companyId) {
      setDraftError(getDraftError());

      return;
    }

    isCreateInFlightRef.current = true;
    setIsDraftLoading(true);
    setDraftError(undefined);

    try {
      const result = await updateDraft({
        variables: {
          input: {
            action: PromoSelectionDraftAction.Create,
            companyId,
          },
        },
      });
      const draftResult = result.data?.result;

      if (!draftResult?.ok || !draftResult.promoSelectionDraftId) {
        throw getDraftError(draftResult?.errors?.[0]?.messages[0]);
      }

      if (isDisposedRef.current || !isMountedRef.current) {
        updateDraft({
          variables: {
            input: {
              action: PromoSelectionDraftAction.Delete,
              companyId,
              promoSelectionDraftId: draftResult.promoSelectionDraftId,
            },
          },
        }).catch(() => undefined);

        return;
      }

      draftIdRef.current = draftResult.promoSelectionDraftId;
      setDraftId(draftResult.promoSelectionDraftId);
      setSelectedCount(draftResult.selectedCount);
      setTotalCount(draftResult.totalCount);
    } catch (error) {
      if (isMountedRef.current) {
        setDraftError(error instanceof Error ? error : getDraftError());
      }
    } finally {
      isCreateInFlightRef.current = false;

      if (isMountedRef.current) {
        setIsDraftLoading(false);
      }
    }
  }, [companyId, updateDraft]);

  const onRetryDraftCreate = useCallback(() => {
    createDraft().catch(() => undefined);
  }, [createDraft]);

  const dispose = useCallback(() => {
    if (disposePromiseRef.current) {
      return disposePromiseRef.current;
    }

    isDisposedRef.current = true;
    const currentDraftId = draftIdRef.current;

    draftIdRef.current = undefined;
    setDraftId(undefined);

    if (!companyId || !currentDraftId) {
      return Promise.resolve();
    }

    const disposePromise = updateDraft({
      variables: {
        input: {
          action: PromoSelectionDraftAction.Delete,
          companyId,
          promoSelectionDraftId: currentDraftId,
        },
      },
    })
      .catch(() => undefined)
      .then(() => undefined);

    disposePromiseRef.current = disposePromise;

    return disposePromise;
  }, [companyId, updateDraft]);

  useEffect(() => {
    if (isPromoReportScope) {
      createDraft().catch(() => undefined);
    }
  }, [createDraft, isPromoReportScope]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      isDisposedRef.current = true;
      const currentDraftId = draftIdRef.current;

      draftIdRef.current = undefined;

      if (companyId && currentDraftId && !disposePromiseRef.current) {
        updateDraft({
          variables: {
            input: {
              action: PromoSelectionDraftAction.Delete,
              companyId,
              promoSelectionDraftId: currentDraftId,
            },
          },
        }).catch(() => undefined);
      }
    };
  }, [companyId, updateDraft]);

  return {
    dispose,
    draftError,
    draftId,
    isDraftLoading,
    onRetryDraftCreate,
    selectedCount,
    setSelectedCount,
    totalCount,
  };
};
